import { useState, useRef, DragEvent } from 'react'
import { Offcanvas } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import Swal from 'sweetalert2'
import { KTIcon } from '../../../_metronic/helpers'
import { useIntl } from 'react-intl'
import { axiosPublic } from '../../utils/axiosClient'
import { LanguagePicker } from '../../../_metronic/partials/layout/header-menus/Languages'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import ReactFlagsSelect from 'react-flags-select'
import { Slider } from 'antd'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

const CATEGORIES = [
  { value: 'BANKING_INSURANCE_FINANCIAL', label: 'Banking/Insurance/Financial products/credit cards' },
  { value: 'REAL_ESTATE', label: 'Real Estate' },
  { value: 'EDUCATION', label: 'Education' },
  { value: 'HEALTH', label: 'Health' },
  { value: 'CONSUMER_GOODS_AUTOMOBILES', label: 'Consumer goods and automobiles' },
  { value: 'COMMUNICATION_BROADCASTING_IT', label: 'Communication/Broadcasting/Entertainment/IT' },
  { value: 'TOURISM_LEISURE', label: 'Tourism and Leisure' },
  { value: 'FOOD_BEVERAGES', label: 'Food and Beverages' },
]

const KYB_DOCS = [
  { key: 'document_ENT_REGN_CERT', label: 'Enterprise Registration Certificate', hint: 'PDF, JPG, PNG up to 5 MB' },
  { key: 'document_TAX_INDT_NO', label: 'Taxpayer Identification Number (TIN)', hint: 'PDF, JPG, PNG up to 5 MB' },
  { key: 'document_VAT_REGN', label: 'VAT Registration / Certificate', hint: 'PDF, JPG, PNG up to 5 MB' },
]

const ID_TYPES = [
  { value: 'PASSPORT', label: 'Passport' },
  { value: 'NATIONAL_ID', label: 'National ID' },
  { value: 'DRIVERS_LICENSE', label: "Driver's License" },
  { value: 'VOTER_ID', label: 'Voter ID' },
]

const STEPS = [
  { title: 'Business Details',          icon: 'briefcase',       required: true },
  { title: 'Authorised Person Details', icon: 'profile-circle',  required: true },
  { title: 'KYB Documents',             icon: 'document',        required: true },
  { title: 'Additional Information',    icon: 'information-2',   required: true },
]

const STEP_MISSING_MSG = [
  'Please complete all required business details before continuing.',
  'Please fill in all contact details, upload your ID, and verify your email address.',
  'Please upload all 3 required KYB documents before continuing.',
]

const QUILL_MODULES = {
  toolbar: [[{ header: [false, 1, 2, 3] }], ['bold', 'italic', 'underline']],
}
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline']

const schema = Yup.object().shape({
  category: Yup.string().required('Business Category is required'),
  legalEntityName: Yup.string()
    .required('Legal entity name is required')
    .min(3, 'Minimum 3 characters')
    .max(100, 'Maximum 100 characters'),
  brandName: Yup.string().max(200, 'Maximum 200 characters'),
  registrationNumber: Yup.string()
    .required('Registration number is required')
    .min(3, 'Minimum 3 characters')
    .max(35, 'Maximum 35 characters'),
  addressLine1: Yup.string().required('Address is required').max(200, 'Maximum 200 characters'),
  addressLine2: Yup.string().max(200, 'Maximum 200 characters'),
  country: Yup.string().required('Country is required'),
  city: Yup.string().required('City is required').max(100, 'Maximum 100 characters'),
  state: Yup.string(),
  postalCode: Yup.string().required('Postal / ZIP code is required').max(10, 'Maximum 10 characters'),
  authorisedPersonName: Yup.string().required('Authorised person name is required').max(50, 'Maximum 50 characters'),
  department: Yup.string().required('Department is required').max(50, 'Maximum 50 characters'),
  contactNumber: Yup.string()
    .required('Contact number is required')
    .min(7, 'Phone number is too short'),
  emailAddress: Yup.string().email('Invalid email format').required('Email address is required').max(254, 'Maximum 254 characters'),
  authorisedIdType: Yup.string().required('ID type is required'),
  additionalType: Yup.string().required('SMS Type is required'),
})

const initialValues = {
  category: '',
  legalEntityName: '',
  brandName: '',
  registrationNumber: '',
  addressLine1: '',
  addressLine2: '',
  country: '',
  city: '',
  state: '',
  postalCode: '',
  authorisedPersonName: '',
  department: '',
  contactNumber: '',
  emailAddress: '',
  authorisedIdType: '',
  additionalType: '',
  additionalFrequency: '',
}

interface DocSlot {
  file: File | null
}

interface KybDocumentUploadProps {
  docs: DocSlot[]
  setDocs: React.Dispatch<React.SetStateAction<DocSlot[]>>
  activeDoc: number
  setActiveDoc: React.Dispatch<React.SetStateAction<number>>
}

function KybDocumentUpload({ docs, setDocs, activeDoc, setActiveDoc }: KybDocumentUploadProps) {
  const intl = useIntl()
  const [isDragging, setIsDragging] = useState(false)
  const [fileError, setFileError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const setFile = (index: number, file: File | null) => {
    setDocs((prev) => prev.map((d, i) => (i === index ? { file } : d)))
  }

  const validateAndSetFile = (index: number, file: File | null) => {
    if (file && file.size > 5 * 1024 * 1024) {
      setFileError('File size must not exceed 5 MB')
      return
    }
    setFileError(null)
    setFile(index, file)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    validateAndSetFile(activeDoc, file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0] ?? null
    validateAndSetFile(activeDoc, file)
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const currentDoc = docs[activeDoc]

  return (
    <div className='row g-0 align-items-stretch' style={{ minHeight: 220 }}>
      {/* Left — document selector tabs (50%) */}
      <div className='col-6 d-flex flex-column gap-3 pe-4'>
        {KYB_DOCS.map((doc, i) => (
          <button
            key={i}
            type='button'
            onClick={() => setActiveDoc(i)}
            title={doc.label}
            className={clsx(
              'btn text-start d-flex align-items-center gap-3 py-3 px-4 rounded-2 position-relative flex-grow-1',
              activeDoc === i ? 'btn-primary' : 'btn-light'
            )}
          >
            <KTIcon
              iconName='document'
              className={clsx('fs-2', activeDoc === i ? 'text-white' : 'text-primary')}
            />
            <div className='overflow-hidden'>
              <div className='fw-semibold fs-7 text-truncate'>
                {doc.label}
                <span className={clsx('ms-1', activeDoc === i ? 'text-white' : 'text-danger')}>*</span>
              </div>
              <div
                className={clsx(
                  'fs-8',
                  docs[i].file
                    ? activeDoc === i
                      ? 'text-white opacity-75'
                      : 'text-success'
                    : activeDoc === i
                    ? 'text-white opacity-50'
                    : 'text-muted'
                )}
              >
                {docs[i].file
                  ? intl.formatMessage({ id: 'ONBOARDING.KYB.DOC_STATUS_UPLOADED' })
                  : intl.formatMessage({ id: 'ONBOARDING.KYB.DOC_STATUS_NOT_UPLOADED' })}
              </div>
            </div>
            {docs[i].file && (
              <div style={{
                position: 'absolute', top: 6, right: 6,
                width: 18, height: 18, borderRadius: '50%',
                backgroundColor: '#50cd89',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <KTIcon iconName='check' className='fs-9 text-white' />
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Right — preview or drag-drop (50%) */}
      <div className='col-6 d-flex flex-column'>
        <div
          className={clsx(
            'flex-grow-1 rounded-2 d-flex flex-column align-items-center justify-content-center p-6',
            isDragging
              ? 'border border-primary border-2 bg-light-primary'
              : 'border border-dashed bg-light'
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {currentDoc.file ? (
            <div className='d-flex flex-column align-items-center gap-3 text-center'>
              <div
                className='d-flex align-items-center justify-content-center rounded-circle bg-light-success'
                style={{ width: 56, height: 56 }}
              >
                <KTIcon iconName='check' className='fs-2 text-success' />
              </div>
              <div>
                <div className='fw-bold text-dark fs-6 mb-1'>{currentDoc.file.name}</div>
                <div className='text-muted fs-7'>{formatBytes(currentDoc.file.size)}</div>
              </div>
              <div className='d-flex gap-2'>
                <label className='btn btn-sm btn-light-primary'>
                  {intl.formatMessage({ id: 'ONBOARDING.KYB.BUTTON_REPLACE' })}
                  <input
                    ref={fileInputRef}
                    type='file'
                    hidden
                    accept='.pdf,.jpg,.jpeg,.png'
                    onChange={handleFileChange}
                  />
                </label>
                <button
                  type='button'
                  className='btn btn-sm btn-light-danger'
                  onClick={() => { setFileError(null); setFile(activeDoc, null) }}
                >
                  <KTIcon iconName='trash' className='fs-4' />
                  {intl.formatMessage({ id: 'ONBOARDING.KYB.BUTTON_DELETE' })}
                </button>
              </div>
            </div>
          ) : (
            <div className='d-flex flex-column align-items-center text-center'>
              <KTIcon iconName='file-up' className='fs-3x text-primary mb-3' />
              <p className='fw-semibold text-dark mb-1 fs-6'>
                {intl.formatMessage({ id: 'COMMON.UPLOAD.DRAG_DROP_TEXT' })}
              </p>
              <p className='text-muted fs-7 mb-1'>{KYB_DOCS[activeDoc].hint}</p>
              <p className='text-muted fs-8 mb-4'>or</p>
              <label className='btn btn-sm btn-primary'>
                {intl.formatMessage({ id: 'ONBOARDING.KYB.BUTTON_BROWSE' })}
                <input
                  ref={fileInputRef}
                  type='file'
                  hidden
                  accept='.pdf,.jpg,.jpeg,.png'
                  onChange={handleFileChange}
                />
              </label>
            </div>
          )}
        </div>
        {fileError && (
          <div className='fv-plugins-message-container mt-2'>
            <span role='alert' className='text-danger fv-help-block'>{fileError}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ── ONBOARDING PREVIEW DRAWER ─────────────────────────────────────────────────

interface OnboardingPreviewDrawerProps {
  show: boolean
  onHide: () => void
  values: typeof initialValues
  docs: DocSlot[]
  emailVerified: boolean
  authorisedIdFile: File | null
  volumeRange: [number, number]
  description: string
}

function OnboardingPreviewDrawer({
  show, onHide, values, docs, emailVerified, authorisedIdFile, volumeRange, description,
}: OnboardingPreviewDrawerProps) {
  const fmtBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const categoryLabel = CATEGORIES.find((c) => c.value === values.category)?.label || '—'

  const addressLines = [
    values.addressLine1,
    values.addressLine2,
    [values.city, values.country, values.postalCode].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join('\n')

  const detailField = (label: string, value: string) => (
    <div className='col-md-6'>
      <div className='text-gray-500 fs-7 mb-1'>{label}</div>
      <div className='fw-bold text-gray-800'>{value || '—'}</div>
    </div>
  )

  return (
    <Offcanvas show={show} onHide={onHide} placement='end' style={{ width: 850 }}>
      <Offcanvas.Header closeButton className='border-bottom'>
        <Offcanvas.Title className='fw-bolder text-dark fs-4'>Application Preview</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body style={{ overflowY: 'auto' }}>
        <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>Business Details</h5>
        <div className='row g-4 mb-8'>
          {detailField('Business Category', categoryLabel)}
          {detailField('Legal Entity Name', values.legalEntityName)}
          {detailField('Brand Name', values.brandName)}
          {detailField('Registration Number', values.registrationNumber)}
          <div className='col-md-6'>
            <div className='text-gray-500 fs-7 mb-1'>Address</div>
            <div className='fw-bold text-gray-800' style={{ whiteSpace: 'pre-line' }}>
              {addressLines || '—'}
            </div>
          </div>
        </div>

        <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>Authorised Person Details</h5>
        <div className='row g-4 mb-8'>
          {detailField('Authorised Person Name', values.authorisedPersonName)}
          {detailField('Department', values.department)}
          {detailField('ID Type', ID_TYPES.find((t) => t.value === values.authorisedIdType)?.label || '—')}
          <div className='col-md-6'>
            <div className='text-gray-500 fs-7 mb-1'>ID Document</div>
            <div className='fw-bold text-gray-800 d-flex align-items-center gap-2'>
              {authorisedIdFile ? (
                <span className='badge badge-light-success fs-8'>Uploaded — {authorisedIdFile.name}</span>
              ) : (
                <span className='badge badge-light-danger fs-8'>Not uploaded</span>
              )}
            </div>
          </div>
          <div className='col-md-6'>
            <div className='text-gray-500 fs-7 mb-1'>Contact Number</div>
            <div className='fw-bold text-gray-800'>
              {values.contactNumber ? `+${values.contactNumber}` : '—'}
            </div>
          </div>
          <div className='col-md-6'>
            <div className='text-gray-500 fs-7 mb-1'>Email Address</div>
            <div className='fw-bold text-gray-800 d-flex align-items-center gap-2'>
              {values.emailAddress || '—'}
              {emailVerified && (
                <span className='badge badge-light-success fs-8 fw-semibold d-inline-flex align-items-center gap-1'>
                  <KTIcon iconName='check' className='fs-9 text-success' />
                  Verified
                </span>
              )}
            </div>
          </div>
        </div>

        <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>KYB Documents</h5>
        <div className='d-flex flex-column gap-3 mb-8'>
          {KYB_DOCS.map((doc, i) => (
            <div
              key={i}
              className='d-flex align-items-center justify-content-between border border-dashed border-gray-300 rounded px-5 py-4'
            >
              <div className='d-flex align-items-center gap-3'>
                <KTIcon
                  iconName='document'
                  className={clsx('fs-2', docs[i].file ? 'text-primary' : 'text-muted')}
                />
                <div>
                  <div className='fw-semibold text-gray-700 fs-7'>{doc.label}</div>
                  {docs[i].file ? (
                    <div className='text-muted fs-8'>
                      {docs[i].file!.name} · {fmtBytes(docs[i].file!.size)}
                    </div>
                  ) : (
                    <div className='text-muted fs-8'>Not uploaded</div>
                  )}
                </div>
              </div>
              <div className='d-flex align-items-center gap-2'>
                {docs[i].file ? (
                  <>
                    <span className='badge badge-light-success fs-8'>Uploaded</span>
                    <button
                      type='button'
                      className='btn btn-sm btn-icon btn-light-info'
                      onClick={() => {
                        const url = URL.createObjectURL(docs[i].file!)
                        window.open(url, '_blank')
                      }}
                    >
                      <KTIcon iconName='eye' className='fs-3' />
                    </button>
                  </>
                ) : (
                  <span className='badge badge-light-danger fs-8'>Required</span>
                )}
              </div>
            </div>
          ))}
        </div>

        <h5 className='fw-bold text-gray-700 mb-4 border-bottom pb-2'>Additional Information</h5>
        <div className='row g-4 mb-4'>
          {detailField('SMS Type', values.additionalType
            ? values.additionalType.charAt(0) + values.additionalType.slice(1).toLowerCase()
            : '—')}
          {detailField('SMS Volume', `${volumeRange[0].toLocaleString()} – ${volumeRange[1].toLocaleString()}`)}
          {detailField('Frequency', values.additionalFrequency
            ? values.additionalFrequency.charAt(0) + values.additionalFrequency.slice(1).toLowerCase()
            : '—')}
        </div>
        {description.replace(/<[^>]*>/g, '').trim() && (
          <div className='mb-4'>
            <div className='text-gray-500 fs-7 mb-2'>Description</div>
            <div
              className='fw-normal text-gray-800 fs-7 border border-gray-300 rounded p-4'
              dangerouslySetInnerHTML={{ __html: description }}
            />
          </div>
        )}
      </Offcanvas.Body>

      <div className='border-top p-5 d-flex justify-content-end'>
        <button type='button' className='btn btn-light' onClick={onHide}>
          Close
        </button>
      </div>
    </Offcanvas>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

export function OnboardingPage() {
  const intl = useIntl()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const [volumeRange, setVolumeRange] = useState<[number, number]>([0, 0])
  const [description, setDescription] = useState('')
  const theme = localStorage.getItem('kt_theme_mode_value')

  const [authorisedIdFile, setAuthorisedIdFile] = useState<File | null>(null)
  const [docs, setDocs] = useState<DocSlot[]>([{ file: null }, { file: null }, { file: null }])
  const [activeDoc, setActiveDoc] = useState(0)

  // Email OTP state
  const [emailOtpSent, setEmailOtpSent] = useState(false)
  const [emailVerified, setEmailVerified] = useState(false)
  const [verifiedToken, setVerifiedToken] = useState<string | null>(null)
  const [emailOtpInputs, setEmailOtpInputs] = useState(['', '', '', '', '', ''])
  const [emailOtpError, setEmailOtpError] = useState<string | null>(null)
  const [resendDisabled, setResendDisabled] = useState(false)
  const [resendTimer, setResendTimer] = useState(0)

  const formatResendTimer = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  const startResendCooldown = () => {
    setResendDisabled(true)
    setResendTimer(60)
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval)
          setResendDisabled(false)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleSendEmailOtp = async () => {
    setEmailOtpError(null)
    setEmailOtpInputs(['', '', '', '', '', ''])
    try {
      await axiosPublic.get('/kyb/verify-email/', {
        params: { email: formik.values.emailAddress },
      })
      setEmailOtpSent(true)
      startResendCooldown()
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data?.data?.detail ??
        intl.formatMessage({ id: 'COMMON.MESSAGES.SOMETHING_WENT_WRONG' })
      Swal.fire({
        title: intl.formatMessage({ id: 'COMMON.ALERTS.ERROR' }),
        text: msg,
        icon: 'error',
      })
    }
  }

  const handleVerifyEmailOtp = async () => {
    if (emailOtpInputs.join('').length < 6) return
    try {
      const { data } = await axiosPublic.post('/kyb/verify-email/', {
        email: formik.values.emailAddress,
        otp: emailOtpInputs.join(''),
      })
      setVerifiedToken(data.data.verified_token)
      setEmailVerified(true)
      setEmailOtpSent(false)
      setEmailOtpError(null)
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.otp?.[0] ||
        intl.formatMessage({ id: 'COMMON.MESSAGES.SOMETHING_WENT_WRONG' })
      setEmailOtpError(msg)
    }
  }

  const onEmailOtpKeyUp = (
    e: React.KeyboardEvent,
    prevIdx: number | null,
    nextIdx: number | null
  ) => {
    if (e.keyCode === 8 || e.keyCode === 37) {
      if (prevIdx !== null) {
        const el = document.getElementById(`emailOtp${prevIdx + 1}`) as HTMLInputElement
        el?.select()
      }
    } else if (
      (e.keyCode >= 48 && e.keyCode <= 57) ||
      (e.keyCode >= 65 && e.keyCode <= 90) ||
      (e.keyCode >= 96 && e.keyCode <= 105) ||
      e.keyCode === 39
    ) {
      if (nextIdx !== null) {
        const el = document.getElementById(`emailOtp${nextIdx + 1}`) as HTMLInputElement
        el?.select()
      }
    }
  }

  const handleEmailOtpPaste = (ev: React.ClipboardEvent) => {
    const clip = ev.clipboardData.getData('text')
    if (!isNaN(Number(clip)) && Number.isInteger(parseInt(clip)) && clip.length >= 6) {
      setEmailOtpInputs([clip[0], clip[1], clip[2], clip[3], clip[4], clip[5]])
      const last = document.getElementById('emailOtp6') as HTMLInputElement
      last?.select()
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: async (values) => {
      setLoading(true)

      if (!emailVerified || !verifiedToken) {
        Swal.fire({
          title: intl.formatMessage({ id: 'COMMON.ALERTS.ERROR' }),
          text: intl.formatMessage({ id: 'ONBOARDING.KYB.EMAIL_NOT_VERIFIED' }),
          icon: 'warning',
        })
        setLoading(false)
        return
      }

      if (docs.some((d) => !d.file)) {
        Swal.fire({
          title: intl.formatMessage({ id: 'COMMON.ALERTS.ERROR' }),
          text: intl.formatMessage({ id: 'ONBOARDING.KYB.DOCS_REQUIRED' }),
          icon: 'warning',
        })
        setLoading(false)
        return
      }

      const plainDescription = description.replace(/<[^>]*>/g, '').trim()
      if (!plainDescription) {
        Swal.fire({
          title: intl.formatMessage({ id: 'COMMON.ALERTS.ERROR' }),
          text: intl.formatMessage({ id: 'ONBOARDING.KYB.DESCRIPTION_REQUIRED' }),
          icon: 'warning',
        })
        setLoading(false)
        return
      }

      try {
        const formData = new FormData()
        formData.append('email', values.emailAddress)
        formData.append('verified_token', verifiedToken)
        formData.append('registration_number', values.registrationNumber)
        formData.append('business_name', values.legalEntityName)
        if (values.brandName) formData.append('brand_name', values.brandName)
        formData.append('resource_type', 'TM')
        formData.append('category', values.category)
        formData.append('country', values.country)
        formData.append('address_line1', values.addressLine1)
        if (values.addressLine2) formData.append('address_line2', values.addressLine2)
        formData.append('city', values.city)
        if (values.state) formData.append('state', values.state)
        formData.append('zip_code', values.postalCode)
        formData.append('authorised_person_name', values.authorisedPersonName)
        formData.append('department', values.department)
        formData.append('contact_phone', '+' + values.contactNumber)
        if (docs[0].file) formData.append('document_ENT_REGN_CERT', docs[0].file)
        if (docs[1].file) formData.append('document_TAX_INDT_NO', docs[1].file)
        if (docs[2].file) formData.append('document_VAT_REGN', docs[2].file)
        formData.append('sms_type', values.additionalType)
        if (values.additionalFrequency) formData.append('additional_frequency', values.additionalFrequency)
        formData.append('volume_min', String(volumeRange[0]))
        formData.append('volume_max', String(volumeRange[1]))
        formData.append('description', description)
        formData.append('authorised_person_id_type', values.authorisedIdType)
        if (authorisedIdFile) formData.append('document_authorised_person_id', authorisedIdFile)

        await axiosPublic.post('/kyb/register/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })

        await Swal.fire({
          title: intl.formatMessage({ id: 'ONBOARDING.KYB.SUCCESS_TITLE' }),
          text: intl.formatMessage({ id: 'ONBOARDING.KYB.SUCCESS_MESSAGE' }),
          icon: 'success',
          confirmButtonText: intl.formatMessage({ id: 'COMMON.BUTTON.OK' }),
        })
        navigate('/auth')
      } catch (err: any) {
        setEmailVerified(false)
        setVerifiedToken(null)
        setEmailOtpSent(false)
        setEmailOtpInputs(['', '', '', '', '', ''])
        setEmailOtpError(null)
        setResendDisabled(false)
        setResendTimer(0)
        const msg =
          err?.response?.data?.data?.detail ||
          err?.response?.data?.message ||
          intl.formatMessage({ id: 'COMMON.MESSAGES.SOMETHING_WENT_WRONG' })
        Swal.fire({
          title: intl.formatMessage({ id: 'ONBOARDING.KYB.ERROR_TITLE' }),
          text: msg,
          icon: 'error',
          footer: intl.formatMessage({ id: 'ONBOARDING.KYB.RESUBMIT_REVERIFY' }),
        })
      } finally {
        setLoading(false)
      }
    },
  })

  const field = (name: keyof typeof initialValues) => ({
    ...formik.getFieldProps(name),
    className: clsx('form-control bg-transparent', {
      'is-invalid': formik.touched[name] && formik.errors[name],
    }),
  })

  const selectField = (name: keyof typeof initialValues) => ({
    ...formik.getFieldProps(name),
    className: clsx('form-select bg-transparent', {
      'is-invalid': formik.touched[name] && formik.errors[name],
    }),
  })

  const errorBlock = (name: keyof typeof initialValues) =>
    formik.touched[name] && formik.errors[name] ? (
      <div className='fv-plugins-message-container'>
        <div className='fv-help-block'>
          <span role='alert'>{formik.errors[name]}</span>
        </div>
      </div>
    ) : null

  const v = formik.values
  const stepComplete = [
    !!(v.category && v.legalEntityName && v.registrationNumber &&
      v.addressLine1 && v.country && v.city && v.postalCode),
    !!(v.authorisedPersonName && v.department && v.contactNumber &&
      v.authorisedIdType && authorisedIdFile !== null && emailVerified),
    docs.every((d) => d.file !== null),
    !!(v.additionalType && description.replace(/<[^>]*>/g, '').trim()),
  ]

  const stepUnlocked = stepComplete.map((_, i) =>
    i === 0 || stepComplete.slice(0, i).every(Boolean)
  )

  const handleContinue = (stepIndex: number) => {
    if (!stepComplete[stepIndex]) {
      Swal.fire({ title: 'Incomplete', text: STEP_MISSING_MSG[stepIndex], icon: 'warning' })
      return
    }
    setActiveStep(stepIndex + 1)
  }

  const handleFillTestData = () => {
    const ts = Date.now().toString().slice(-6)
    formik.setValues({
      category: 'BANKING_INSURANCE_FINANCIAL',
      legalEntityName: `Pinnacle Finserv ${ts} Pvt Ltd`,
      brandName: `PinFin${ts}`,
      registrationNumber: `U${ts}MH2020PTC348291`,
      addressLine1: '14 That Luang Road',
      addressLine2: 'Level 3, Tower B',
      country: 'LA',
      city: 'Vientiane',
      state: 'Vientiane Prefecture',
      postalCode: '01000',
      authorisedPersonName: 'Arjun Mehta',
      department: 'Compliance',
      contactNumber: '8562022222222',
      emailAddress: `arjun.mehta${ts}@pinnacle.in`,
      authorisedIdType: 'PASSPORT',
      additionalType: 'TRANSACTIONAL',
      additionalFrequency: 'MONTHLY',
    })
    const dummyFile = (name: string) => new File(['dummy'], name, { type: 'application/pdf' })
    setDocs([
      { file: dummyFile('pinnacle-ent-reg.pdf') },
      { file: dummyFile('pinnacle-tax-id.pdf') },
      { file: dummyFile('pinnacle-vat.pdf') },
    ])
    setAuthorisedIdFile(dummyFile('arjun-mehta-passport.pdf'))
    setDescription('<p>Pinnacle Finserv provides transactional SMS services to banks and NBFCs, primarily for OTP delivery, account alerts, and payment confirmations.</p>')
    setVolumeRange([50000, 500000])
    setActiveStep(0)
  }

  const handleClear = () => {
    formik.resetForm()
    setDocs([{ file: null }, { file: null }, { file: null }])
    setActiveDoc(0)
    setEmailOtpSent(false)
    setEmailVerified(false)
    setVerifiedToken(null)
    setEmailOtpInputs(['', '', '', '', '', ''])
    setEmailOtpError(null)
    setResendDisabled(false)
    setResendTimer(0)
    setVolumeRange([0, 0])
    setDescription('')
    setAuthorisedIdFile(null)
    setActiveStep(0)
  }

  return (
    <div className='min-vh-100 bg-light d-flex flex-column align-items-center pb-10 px-4' style={{ paddingTop: 80 }}>
      {/* Fixed top bar */}
      <div
        className='d-flex align-items-center justify-content-between bg-white'
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200, padding: '10px 24px' }}
      >
        {theme === 'dark'
          ? <img src='/media/logos/Goflipo_white_new.png' alt='GoFlipo' style={{ height: 30 }} />
          : <img src='/media/logos/Goflipo_color_new.png' alt='GoFlipo' style={{ height: 30 }} />
        }
        <div className='d-flex align-items-center gap-3'>
          {process.env.NODE_ENV === 'development' && (
            <button type='button' className='btn btn-sm btn-warning' onClick={handleFillTestData}>
              Fill Test Data
            </button>
          )}
          <LanguagePicker />
        </div>
      </div>

      {/* Card */}
      <div className='card w-100 mb-10' style={{ maxWidth: 920 }}>
        <div className='card-body p-8 p-md-12'>
          {/* Page heading */}
          <div className='mb-10 text-center'>
            <h2 className='fs-1 fw-bolder text-dark mb-2'>
              {intl.formatMessage({ id: 'ONBOARDING.KYB.PAGE_TITLE' })}
            </h2>
            <p className='text-muted fs-6 mb-0'>
              {intl.formatMessage({ id: 'ONBOARDING.KYB.PAGE_SUBTITLE' })}
            </p>
          </div>

          <form
            className='form fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            onSubmit={formik.handleSubmit}
          >
            {/* ── Stepper ── */}
            <div>
              {STEPS.map((step, i) => {
                const isActive = activeStep === i
                const isComplete = stepComplete[i]
                const isUnlocked = stepUnlocked[i]
                const isVisuallyComplete = isComplete && step.required

                const circleStyle: React.CSSProperties = {
                  width: 44,
                  height: 44,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: isVisuallyComplete ? '#e8fff3' : isActive ? '#f1faff' : '#f5f8fa',
                  border: isVisuallyComplete
                    ? '1.5px solid #50cd89'
                    : isActive
                    ? '1.5px solid #009ef7'
                    : isUnlocked
                    ? '1.5px solid #d1d5db'
                    : '1.5px dashed #d1d5db',
                  transition: 'background 0.2s ease, border-color 0.2s ease',
                }

                return (
                  <div key={i} className='d-flex'>
                    {/* LEFT: indicator column */}
                    <div
                      className='d-flex flex-column align-items-center flex-shrink-0'
                      style={{ width: 56 }}
                    >
                      <div style={circleStyle}>
                        <KTIcon
                          iconName={step.icon}
                          className={clsx('fs-3', {
                            'text-success': isVisuallyComplete,
                            'text-primary': !isVisuallyComplete && isActive,
                            'text-gray-500': !isVisuallyComplete && !isActive && isUnlocked,
                            'text-gray-300': !isUnlocked,
                          })}
                        />
                      </div>
                      {i < STEPS.length - 1 && (
                        <div style={{
                          flex: 1,
                          minHeight: 24,
                          marginTop: 4,
                          marginBottom: 4,
                          borderLeft: '2px dashed #e4e6ef',
                        }} />
                      )}
                    </div>

                    {/* RIGHT: header + collapsible body */}
                    <div className='flex-grow-1 pb-8 ps-4' style={{ minWidth: 0 }}>
                      <button
                        type='button'
                        className='d-flex align-items-center justify-content-between w-100 btn btn-flush py-3'
                        onClick={() => {
                          if (!isUnlocked) return
                          setActiveStep(isActive ? -1 : i)
                        }}
                        disabled={!isUnlocked}
                      >
                        <div className='d-flex align-items-center gap-3'>
                          <span
                            className='fs-5 fw-bold'
                            style={{ color: isUnlocked ? '#181c32' : '#b5b5c3' }}
                          >
                            {step.title}
                          </span>
                          {!isUnlocked && (
                            <KTIcon iconName='lock-3' className='fs-8 text-muted' />
                          )}
                          {step.required
                            ? isVisuallyComplete && !isActive
                              ? <span className='badge badge-light-success fs-8 fw-semibold'>Completed</span>
                              : <span className='badge badge-light-danger fs-8 fw-semibold'>Required</span>
                            : <span className='badge badge-light-warning fs-8 fw-semibold'>Optional</span>
                          }
                        </div>
                        {isUnlocked && (
                          <KTIcon
                            iconName={isActive ? 'up' : 'down'}
                            className='fs-4 text-muted'
                          />
                        )}
                      </button>

                      <div className='separator' />

                      {isActive && (
                        <div className='pt-6'>

                          {/* ── Step 0: Business Details ── */}
                          {i === 0 && (
                            <>
                              {/* Business Category */}
                              <div className='fv-row mb-7'>
                                <label className='form-label fs-6 fw-bolder text-dark required'>
                                  {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_CATEGORY' })}
                                </label>
                                <select {...selectField('category')}>
                                  <option value=''>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.SELECT_CATEGORY' })}
                                  </option>
                                  {CATEGORIES.map((cat) => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                  ))}
                                </select>
                                {errorBlock('category')}
                              </div>

                              {/* Legal Entity Name + Brand Name */}
                              <div className='row g-6 mb-7'>
                                <div className='col-md-6'>
                                  <label className='form-label fs-6 fw-bolder text-dark required'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_LEGAL_NAME' })}
                                  </label>
                                  <input
                                    type='text'
                                    maxLength={100}
                                    placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.PLACEHOLDER_LEGAL_NAME' })}
                                    autoComplete='off'
                                    {...field('legalEntityName')}
                                  />
                                  {errorBlock('legalEntityName')}
                                </div>
                                <div className='col-md-6'>
                                  <label className='form-label fs-6 fw-bolder text-dark'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_BRAND_NAME' })}
                                    <span className='text-muted fw-normal ms-1 fs-7'>(optional)</span>
                                  </label>
                                  <input
                                    type='text'
                                    maxLength={200}
                                    placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.PLACEHOLDER_BRAND_NAME' })}
                                    autoComplete='off'
                                    {...field('brandName')}
                                  />
                                  {errorBlock('brandName')}
                                </div>
                              </div>

                              {/* Registration Number */}
                              <div className='fv-row mb-7'>
                                <label className='form-label fs-6 fw-bolder text-dark required'>
                                  {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_REG_NUMBER' })}
                                </label>
                                <input
                                  type='text'
                                  maxLength={35}
                                  placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.PLACEHOLDER_REG_NUMBER' })}
                                  autoComplete='off'
                                  {...field('registrationNumber')}
                                />
                                {errorBlock('registrationNumber')}
                              </div>

                              {/* Registration Address */}
                              <div className='bg-light rounded-2 p-6 mb-2'>
                                <p className='fs-7 fw-semibold text-muted text-uppercase mb-4 ls-1'>
                                  {intl.formatMessage({ id: 'ONBOARDING.KYB.SECTION_ADDRESS' })}
                                </p>

                                <div className='fv-row mb-6'>
                                  <label className='form-label fs-6 fw-bolder text-dark required'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_ADDRESS_LINE1' })}
                                  </label>
                                  <input
                                    type='text'
                                    maxLength={200}
                                    placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.PLACEHOLDER_ADDRESS' })}
                                    autoComplete='off'
                                    {...field('addressLine1')}
                                  />
                                  {errorBlock('addressLine1')}
                                </div>

                                <div className='fv-row mb-6'>
                                  <label className='form-label fs-6 fw-bolder text-dark'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_ADDRESS_LINE2' })}
                                    <span className='text-muted fw-normal ms-1 fs-7'>(optional)</span>
                                  </label>
                                  <input
                                    type='text'
                                    maxLength={200}
                                    placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.PLACEHOLDER_ADDRESS_LINE2' })}
                                    autoComplete='off'
                                    {...field('addressLine2')}
                                  />
                                  {errorBlock('addressLine2')}
                                </div>

                                <div className='row g-6'>
                                  <div className='col-md-4'>
                                    <label className='form-label fs-6 fw-bolder text-dark required'>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_COUNTRY' })}
                                    </label>
                                    <ReactFlagsSelect
                                      selected={formik.values.country}
                                      onSelect={(code) => {
                                        formik.setFieldValue('country', code, true)
                                        formik.setFieldTouched('country', true, false)
                                      }}
                                      searchable
                                      placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.SELECT_COUNTRY' })}
                                      className={clsx('react-flags-select-wrapper', {
                                        'is-invalid': formik.touched.country && formik.errors.country,
                                      })}
                                    />
                                    {errorBlock('country')}
                                  </div>
                                  <div className='col-md-4'>
                                    <label className='form-label fs-6 fw-bolder text-dark required'>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_CITY' })}
                                    </label>
                                    <input
                                      type='text'
                                      maxLength={100}
                                      placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.PLACEHOLDER_CITY' })}
                                      autoComplete='off'
                                      {...field('city')}
                                    />
                                    {errorBlock('city')}
                                  </div>
                                  <div className='col-md-4'>
                                    <label className='form-label fs-6 fw-bolder text-dark required'>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_POSTAL_CODE' })}
                                    </label>
                                    <input
                                      type='text'
                                      maxLength={10}
                                      placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.PLACEHOLDER_POSTAL_CODE' })}
                                      autoComplete='off'
                                      {...field('postalCode')}
                                    />
                                    {errorBlock('postalCode')}
                                  </div>
                                </div>
                              </div>

                              <div className='d-flex justify-content-between mt-6'>
                                <span />
                                <button type='button' className='btn btn-primary' onClick={() => handleContinue(0)}>
                                  {intl.formatMessage({ id: 'COMMON.BUTTON.CONTINUE' })}
                                </button>
                              </div>
                            </>
                          )}

                          {/* ── Step 1: Authorised Person Details ── */}
                          {i === 1 && (
                            <>
                              <div className='row g-6 mb-7'>
                                <div className='col-md-6'>
                                  <label className='form-label fs-6 fw-bolder text-dark required'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_AUTH_PERSON' })}
                                  </label>
                                  <input
                                    type='text'
                                    maxLength={50}
                                    placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.PLACEHOLDER_FULL_NAME' })}
                                    autoComplete='off'
                                    {...field('authorisedPersonName')}
                                  />
                                  {errorBlock('authorisedPersonName')}
                                </div>
                                <div className='col-md-6'>
                                  <label className='form-label fs-6 fw-bolder text-dark required'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_DEPARTMENT' })}
                                  </label>
                                  <input
                                    type='text'
                                    maxLength={50}
                                    placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.PLACEHOLDER_DEPARTMENT' })}
                                    autoComplete='off'
                                    {...field('department')}
                                  />
                                  {errorBlock('department')}
                                </div>
                              </div>

                              <div className='row g-6 mb-7'>
                                <div className='col-md-6'>
                                  <label className='form-label fs-6 fw-bolder text-dark required'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_CONTACT_NUMBER' })}
                                  </label>
                                  <PhoneInput
                                    country='la'
                                    value={formik.values.contactNumber}
                                    onChange={(phone) => formik.setFieldValue('contactNumber', phone)}
                                    onBlur={() => formik.setFieldTouched('contactNumber', true)}
                                    inputClass={clsx('form-control bg-transparent w-100', {
                                      'is-invalid': formik.touched.contactNumber && formik.errors.contactNumber,
                                    })}
                                    inputStyle={{ height: '42px', width: '100%' }}
                                    containerClass='w-100'
                                    enableSearch
                                    searchPlaceholder='Search country...'
                                  />
                                  {errorBlock('contactNumber')}
                                </div>
                                <div className='col-md-6'>
                                  <label className='form-label fs-6 fw-bolder text-dark required d-flex align-items-center gap-2'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_EMAIL' })}
                                    {emailVerified && (
                                      <span className='badge badge-light-success fs-8 fw-semibold d-inline-flex align-items-center gap-1'>
                                        <KTIcon iconName='check' className='fs-9 text-success' />
                                        {intl.formatMessage({ id: 'ONBOARDING.KYB.EMAIL_VERIFIED' })}
                                      </span>
                                    )}
                                  </label>
                                  <div style={{ position: 'relative' }}>
                                    <input
                                      type='email'
                                      maxLength={254}
                                      placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.PLACEHOLDER_EMAIL' })}
                                      autoComplete='off'
                                      {...field('emailAddress')}
                                      disabled={emailVerified}
                                      style={{ paddingRight: emailVerified ? undefined : 130 }}
                                    />
                                    {!emailVerified && (
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-primary text-nowrap'
                                        style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)' }}
                                        disabled={
                                          !formik.values.emailAddress ||
                                          !!formik.errors.emailAddress ||
                                          (emailOtpSent && resendDisabled)
                                        }
                                        onClick={handleSendEmailOtp}
                                      >
                                        {emailOtpSent
                                          ? resendDisabled
                                            ? formatResendTimer(resendTimer)
                                            : intl.formatMessage({ id: 'ONBOARDING.KYB.EMAIL_RESEND_OTP' })
                                          : intl.formatMessage({ id: 'ONBOARDING.KYB.EMAIL_SEND_OTP' })}
                                      </button>
                                    )}
                                  </div>
                                  {errorBlock('emailAddress')}
                                  {emailOtpSent && !emailVerified && (
                                    <button
                                      type='button'
                                      className='btn btn-link btn-sm p-0 text-muted fs-7 mt-1'
                                      onClick={() => {
                                        setEmailOtpSent(false)
                                        setEmailOtpInputs(['', '', '', '', '', ''])
                                        setEmailOtpError(null)
                                        setResendDisabled(false)
                                        setResendTimer(0)
                                      }}
                                    >
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.CHANGE_EMAIL' })}
                                    </button>
                                  )}
                                </div>
                              </div>

                              {/* Email OTP verification panel */}
                              {emailOtpSent && !emailVerified && (
                                <div className='bg-light-primary border border-primary border-opacity-25 rounded-2 p-6 mb-7'>
                                  <div className='d-flex align-items-start gap-3 mb-4'>
                                    <div
                                      className='d-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10'
                                      style={{ width: 36, height: 36, flexShrink: 0 }}
                                    >
                                      <KTIcon iconName='sms' className='fs-4 text-primary' />
                                    </div>
                                    <div>
                                      <p className='fs-6 fw-bold text-dark mb-1'>
                                        {intl.formatMessage(
                                          { id: 'ONBOARDING.KYB.EMAIL_OTP_SENT_TO' },
                                          { email: formik.values.emailAddress }
                                        )}
                                      </p>
                                      <p className='fs-7 text-muted mb-0'>
                                        {intl.formatMessage({ id: 'ONBOARDING.KYB.EMAIL_OTP_LABEL' })}
                                      </p>
                                    </div>
                                  </div>

                                  <div className='d-flex gap-5 mb-4'>
                                    {emailOtpInputs.map((val, idx) => (
                                      <input
                                        key={idx}
                                        id={`emailOtp${idx + 1}`}
                                        type='text'
                                        maxLength={1}
                                        autoComplete='off'
                                        value={val}
                                        className='form-control bg-white h-60px fs-2x text-center fw-bold'
                                        onChange={(e) => {
                                          const newInputs = [...emailOtpInputs]
                                          newInputs[idx] = e.target.value
                                          setEmailOtpInputs(newInputs)
                                        }}
                                        onKeyUp={(e) =>
                                          onEmailOtpKeyUp(e, idx > 0 ? idx - 1 : null, idx < 5 ? idx + 1 : null)
                                        }
                                        onPaste={handleEmailOtpPaste}
                                      />
                                    ))}
                                  </div>

                                  {emailOtpError && (
                                    <div className='alert alert-danger py-2 px-4 mb-4 d-flex align-items-center gap-2'>
                                      <KTIcon iconName='cross-circle' className='fs-5 text-danger' />
                                      <span className='fs-7'>{emailOtpError}</span>
                                    </div>
                                  )}

                                  <button
                                    type='button'
                                    className='btn btn-primary btn-sm px-6'
                                    disabled={emailOtpInputs.join('').length < 6}
                                    onClick={handleVerifyEmailOtp}
                                  >
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.EMAIL_VERIFY_OTP' })}
                                  </button>
                                </div>
                              )}

                              {/* ID Type + ID Document */}
                              <div className='row g-6 mb-7'>
                                <div className='col-md-6'>
                                  <label className='form-label fs-6 fw-bolder text-dark required'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_ID_TYPE' })}
                                  </label>
                                  <select {...selectField('authorisedIdType')}>
                                    <option value=''>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.DIRECTOR_ID_TYPE_PLACEHOLDER' })}
                                    </option>
                                    {ID_TYPES.map((t) => (
                                      <option key={t.value} value={t.value}>{t.label}</option>
                                    ))}
                                  </select>
                                  {errorBlock('authorisedIdType')}
                                </div>
                                <div className='col-md-6'>
                                  <label className='form-label fs-6 fw-bolder text-dark required'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_ID_DOCUMENT' })}
                                  </label>
                                  {authorisedIdFile ? (
                                    <div
                                      className='d-flex align-items-center gap-2 w-100'
                                      style={{ height: 42, border: '1px solid #e4e6ef', borderRadius: '0.475rem', padding: '0 10px' }}
                                    >
                                      <KTIcon iconName='file' className='fs-5 text-primary flex-shrink-0' />
                                      <span className='text-dark fs-7 fw-semibold text-truncate flex-grow-1'>
                                        {authorisedIdFile.name}
                                      </span>
                                      <button
                                        type='button'
                                        className='btn btn-sm btn-icon btn-flush ms-auto flex-shrink-0'
                                        onClick={() => setAuthorisedIdFile(null)}
                                      >
                                        <KTIcon iconName='trash' className='fs-5 text-danger' />
                                      </button>
                                    </div>
                                  ) : (
                                    <label
                                      className='btn btn-light w-100 text-start d-flex align-items-center gap-2 mb-0'
                                      style={{ height: 42, border: '1px solid #e4e6ef' }}
                                    >
                                      <KTIcon iconName='file-up' className='fs-4 text-primary' />
                                      <span className='text-muted fs-7'>
                                        {intl.formatMessage({ id: 'ONBOARDING.KYB.DIRECTOR_ID_PHOTO_PLACEHOLDER' })}
                                      </span>
                                      <input
                                        type='file'
                                        hidden
                                        accept='.pdf,.jpg,.jpeg,.png'
                                        onChange={(e) => setAuthorisedIdFile(e.target.files?.[0] ?? null)}
                                      />
                                    </label>
                                  )}
                                </div>
                              </div>

                              <div className='d-flex justify-content-between mt-6'>
                                <button type='button' className='btn btn-light' onClick={() => setActiveStep(0)}>
                                  {intl.formatMessage({ id: 'COMMON.BUTTON.PREVIOUS' })}
                                </button>
                                <button type='button' className='btn btn-primary' onClick={() => handleContinue(1)}>
                                  {intl.formatMessage({ id: 'COMMON.BUTTON.CONTINUE' })}
                                </button>
                              </div>
                            </>
                          )}

                          {/* ── Step 2: KYB Documents ── */}
                          {i === 2 && (
                            <>
                              <p className='text-muted fs-7 mb-5'>
                                {intl.formatMessage({ id: 'ONBOARDING.KYB.DOCUMENT_INSTRUCTION' })}
                              </p>
                              <KybDocumentUpload
                                docs={docs}
                                setDocs={setDocs}
                                activeDoc={activeDoc}
                                setActiveDoc={setActiveDoc}
                              />
                              <div className='d-flex justify-content-between mt-6'>
                                <button type='button' className='btn btn-light' onClick={() => setActiveStep(1)}>
                                  {intl.formatMessage({ id: 'COMMON.BUTTON.PREVIOUS' })}
                                </button>
                                <button type='button' className='btn btn-primary' onClick={() => handleContinue(2)}>
                                  {intl.formatMessage({ id: 'COMMON.BUTTON.CONTINUE' })}
                                </button>
                              </div>
                            </>
                          )}

                          {/* ── Step 3: Additional Information ── */}
                          {i === 3 && (
                            <>
                              <div className='row g-5 mb-7'>
                                <div className='col-md-4'>
                                  <label className='form-label fs-6 fw-bolder text-dark required'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_ADDITIONAL_TYPE' })}
                                  </label>
                                  <select {...selectField('additionalType')}>
                                    <option value=''>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.SELECT_TYPE' })}
                                    </option>
                                    <option value='PROMOTIONAL'>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.TYPE_PROMOTIONAL' })}
                                    </option>
                                    <option value='TRANSACTIONAL'>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.TYPE_TRANSACTIONAL' })}
                                    </option>
                                    <option value='BOTH'>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.TYPE_BOTH' })}
                                    </option>
                                  </select>
                                  {errorBlock('additionalType')}
                                </div>
                                <div className='col-md-5'>
                                  <label className='form-label fs-6 fw-bolder text-dark d-flex justify-content-between align-items-center gap-1 flex-nowrap'>
                                    <span className='text-nowrap'>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_VOLUME' })}
                                    </span>
                                    <span className='fw-normal text-muted fs-8 text-nowrap ms-1'>
                                      {volumeRange[0].toLocaleString()} – {volumeRange[1].toLocaleString()}
                                    </span>
                                  </label>
                                  <div style={{
                                    border: '1px solid #e4e6ef',
                                    borderRadius: '0.475rem',
                                    background: 'transparent',
                                    height: 42,
                                    display: 'flex',
                                    alignItems: 'center',
                                    padding: '0 14px',
                                  }}>
                                    <Slider
                                      range
                                      min={0}
                                      max={1000000}
                                      step={1000}
                                      value={volumeRange}
                                      onChange={(val) => setVolumeRange(val as [number, number])}
                                      tooltip={{ formatter: (val) => val?.toLocaleString() }}
                                      style={{ width: '100%' }}
                                    />
                                  </div>
                                </div>
                                <div className='col-md-3'>
                                  <label className='form-label fs-6 fw-bolder text-dark'>
                                    {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_FREQUENCY' })}
                                  </label>
                                  <select
                                    {...formik.getFieldProps('additionalFrequency')}
                                    className='form-select bg-transparent'
                                  >
                                    <option value=''>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.SELECT_FREQUENCY' })}
                                    </option>
                                    <option value='DAILY'>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.FREQ_DAILY' })}
                                    </option>
                                    <option value='WEEKLY'>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.FREQ_WEEKLY' })}
                                    </option>
                                    <option value='MONTHLY'>
                                      {intl.formatMessage({ id: 'ONBOARDING.KYB.FREQ_MONTHLY' })}
                                    </option>
                                  </select>
                                </div>
                              </div>

                              <div className='fv-row mt-5 mb-8'>
                                <label className='form-label fs-6 fw-bolder text-dark required'>
                                  {intl.formatMessage({ id: 'ONBOARDING.KYB.FIELD_DESCRIPTION' })}
                                </label>
                                <div className='kyb-description-quill'>
                                  <style>{`.kyb-description-quill .ql-editor { min-height: 160px; }`}</style>
                                  <ReactQuill
                                    theme='snow'
                                    value={description}
                                    onChange={setDescription}
                                    modules={QUILL_MODULES}
                                    formats={QUILL_FORMATS}
                                    placeholder={intl.formatMessage({ id: 'ONBOARDING.KYB.DESCRIPTION_PLACEHOLDER' })}
                                  />
                                </div>
                                <p className='text-muted fs-8 mt-2 mb-0'>
                                  {intl.formatMessage({ id: 'ONBOARDING.KYB.DESCRIPTION_HINT' })}
                                </p>
                              </div>

                              <div className='d-flex justify-content-between mt-6'>
                                <button type='button' className='btn btn-light' onClick={() => setActiveStep(2)}>
                                  {intl.formatMessage({ id: 'COMMON.BUTTON.PREVIOUS' })}
                                </button>
                              </div>
                            </>
                          )}

                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* ── Action buttons — always visible outside accordion ── */}
            <div className='d-flex gap-3 justify-content-end mt-8 pt-6'>
              <button
                type='button'
                className='btn btn-light-danger'
                disabled={formik.isSubmitting}
                onClick={handleClear}
              >
                {intl.formatMessage({ id: 'ONBOARDING.KYB.BUTTON_CLEAR' })}
              </button>
              <button
                type='button'
                className='btn btn-secondary'
                disabled={formik.isSubmitting}
                onClick={() => setPreviewOpen(true)}
              >
                <KTIcon iconName='eye' className='fs-3 me-2' />
                {intl.formatMessage({ id: 'ONBOARDING.KYB.BUTTON_PREVIEW' })}
              </button>
              <button
                type='submit'
                className='btn btn-primary btn-lg'
                disabled={formik.isSubmitting}
              >
                {!loading ? (
                  <span className='indicator-label'>
                    {intl.formatMessage({ id: 'ONBOARDING.KYB.BUTTON_SUBMIT' })}
                  </span>
                ) : (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    {intl.formatMessage({ id: 'COMMON.MESSAGES.PLEASE_WAIT' })}
                    <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <OnboardingPreviewDrawer
        show={previewOpen}
        onHide={() => setPreviewOpen(false)}
        values={formik.values}
        docs={docs}
        emailVerified={emailVerified}
        authorisedIdFile={authorisedIdFile}
        volumeRange={volumeRange}
        description={description}
      />
    </div>
  )
}
